import React, { useMemo, useState } from 'react';
import { ArrowUp, ArrowDown, Minus, Package, TrendingUp, TrendingDown, Equal, AlertTriangle, Warehouse, Store, GitCompare } from 'lucide-react';
import type { ProductWithLocationQuantities, ComparisonStats } from '../../utils/locationInventory';
import { OverviewMetricCard } from './OverviewMetricCard';

interface ColorInfo {
  id: string;
  name: string;
  hex_code?: string;
}

interface SizeInfo {
  id: string;
  name: string;
  sort_order?: number;
}

interface LocationComparisonTableProps {
  products: ProductWithLocationQuantities[];
  warehouseLocation: { id: string; name: string };
  comparisonLocation: { id: string; name: string };
  colors: ColorInfo[];
  sizes: SizeInfo[];
  searchTerm: string;
  stats: ComparisonStats;
}

interface ProductComparisonGroup {
  productName: string;
  referenceCode: string;
  category: string;
  imageUrl?: string;
  sellPrice: number;
  buyPrice: number;
  warehouseQty: number;
  otherQty: number;
  difference: number;
  colors: ColorComparisonVariant[];
}

interface ColorComparisonVariant {
  colorId: string;
  colorName: string;
  hexCode?: string;
  warehouseQty: number;
  otherQty: number;
  difference: number;
  sizeBreakdown: { size: string; warehouseQty: number; otherQty: number; difference: number }[];
}

export const LocationComparisonTable: React.FC<LocationComparisonTableProps> = ({
  products,
  warehouseLocation,
  comparisonLocation,
  colors,
  sizes,
  searchTerm,
  stats
}) => {
  const [imgErrors, setImgErrors] = useState<Set<string>>(new Set());

  const getColorInfo = (colorId: string | undefined): ColorInfo | undefined => {
    if (!colorId) return undefined;
    return colors.find(c => c.id === colorId);
  };

  const getSizeName = (sizeId: string | undefined): string | undefined => {
    if (!sizeId) return undefined;
    return sizes.find(s => s.id === sizeId)?.name;
  };

  const sortedSizes = useMemo(() => {
    return [...sizes].sort((a, b) => {
      if (a.sort_order !== undefined && b.sort_order !== undefined) {
        return a.sort_order - b.sort_order;
      }
      return a.name.localeCompare(b.name);
    });
  }, [sizes]);

  const productGroups = useMemo(() => {
    const groups: Record<string, ProductComparisonGroup> = {};

    products.forEach(product => {
      const groupKey = product.name || 'Unnamed Product';
      const warehouseQty = product.locationQuantities[warehouseLocation.id] || 0;
      const otherQty = product.locationQuantities[comparisonLocation.id] || 0;

      if (!groups[groupKey]) {
        groups[groupKey] = {
          productName: product.name || 'Unnamed Product',
          referenceCode: product.reference_code || '',
          category: product.category || '',
          imageUrl: product.image_url,
          sellPrice: product.sell_price || 0,
          buyPrice: product.buy_price || 0,
          warehouseQty: 0,
          otherQty: 0,
          difference: 0,
          colors: []
        };
      }

      const colorInfo = getColorInfo(product.color_id);
      const colorKey = product.color_id || 'no-color';
      const sizeName = getSizeName(product.size_id) || 'No Size';

      let colorVariant = groups[groupKey].colors.find(c => c.colorId === colorKey);
      if (!colorVariant) {
        colorVariant = {
          colorId: colorKey,
          colorName: colorInfo?.name || '-',
          hexCode: colorInfo?.hex_code,
          warehouseQty: 0,
          otherQty: 0,
          difference: 0,
          sizeBreakdown: []
        };
        groups[groupKey].colors.push(colorVariant);
      }

      colorVariant.warehouseQty += warehouseQty;
      colorVariant.otherQty += otherQty;
      colorVariant.difference = colorVariant.otherQty - colorVariant.warehouseQty;

      let sizeEntry = colorVariant.sizeBreakdown.find(s => s.size === sizeName);
      if (!sizeEntry) {
        sizeEntry = { size: sizeName, warehouseQty: 0, otherQty: 0, difference: 0 };
        colorVariant.sizeBreakdown.push(sizeEntry);
      }
      sizeEntry.warehouseQty += warehouseQty;
      sizeEntry.otherQty += otherQty;
      sizeEntry.difference = sizeEntry.otherQty - sizeEntry.warehouseQty;

      groups[groupKey].warehouseQty += warehouseQty;
      groups[groupKey].otherQty += otherQty;
      groups[groupKey].difference = groups[groupKey].otherQty - groups[groupKey].warehouseQty;

      if (product.reference_code && !groups[groupKey].referenceCode) {
        groups[groupKey].referenceCode = product.reference_code;
      }
      if (product.image_url && !groups[groupKey].imageUrl) {
        groups[groupKey].imageUrl = product.image_url;
      }
      if (product.sell_price && groups[groupKey].sellPrice === 0) {
        groups[groupKey].sellPrice = product.sell_price;
      }
      if (product.buy_price && groups[groupKey].buyPrice === 0) {
        groups[groupKey].buyPrice = product.buy_price;
      }
      if (product.category && !groups[groupKey].category) {
        groups[groupKey].category = product.category;
      }
    });

    Object.values(groups).forEach(group => {
      group.colors.sort((a, b) => a.colorName.localeCompare(b.colorName));
      group.colors.forEach(color => {
        color.sizeBreakdown.sort((a, b) => {
          const aIdx = sortedSizes.findIndex(s => s.name === a.size);
          const bIdx = sortedSizes.findIndex(s => s.name === b.size);
          if (aIdx === -1 && bIdx === -1) return a.size.localeCompare(b.size);
          if (aIdx === -1) return 1;
          if (bIdx === -1) return -1;
          return aIdx - bIdx;
        });
      });
    });

    return Object.values(groups).sort((a, b) => a.productName.localeCompare(b.productName));
  }, [products, warehouseLocation.id, comparisonLocation.id, colors, sizes, sortedSizes]);

  const filteredGroups = useMemo(() => {
    if (!searchTerm) return productGroups;
    const searchLower = searchTerm.toLowerCase();
    return productGroups.filter(group =>
      group.productName.toLowerCase().includes(searchLower) ||
      group.referenceCode?.toLowerCase().includes(searchLower) ||
      group.category?.toLowerCase().includes(searchLower) ||
      group.colors.some(c => c.colorName.toLowerCase().includes(searchLower))
    );
  }, [productGroups, searchTerm]);

  const sizeColumns = useMemo(() => {
    const usedSizes = new Set<string>();
    productGroups.forEach(group => {
      group.colors.forEach(color => {
        color.sizeBreakdown.forEach(sb => {
          if (sb.size !== 'No Size') {
            usedSizes.add(sb.size);
          }
        });
      });
    });

    const hasNoSize = productGroups.some(group =>
      group.colors.some(color => color.sizeBreakdown.some(sb => sb.size === 'No Size'))
    );

    const orderedSizes = sortedSizes.filter(s => usedSizes.has(s.name)).map(s => s.name);
    if (hasNoSize) orderedSizes.push('No Size');
    return orderedSizes;
  }, [productGroups, sortedSizes]);

  const DifferenceIndicator: React.FC<{ diff: number; showText?: boolean }> = ({ diff, showText = true }) => {
    if (diff > 0) {
      return (
        <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400 font-semibold">
          <ArrowUp className="h-3.5 w-3.5" />
          {showText && <span>+{diff}</span>}
        </span>
      );
    }
    if (diff < 0) {
      return (
        <span className="inline-flex items-center gap-1 text-rose-600 dark:text-rose-400 font-semibold">
          <ArrowDown className="h-3.5 w-3.5" />
          {showText && <span>{diff}</span>}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 text-slate-400 dark:text-slate-500">
        <Minus className="h-3.5 w-3.5" />
        {showText && <span>0</span>}
      </span>
    );
  };

  const getRowBackground = (difference: number) => {
    if (difference > 0) return 'bg-emerald-50/50 dark:bg-emerald-900/10';
    if (difference < 0) return 'bg-rose-50/50 dark:bg-rose-900/10';
    return '';
  };

  if (filteredGroups.length === 0) {
    return (
      <div className="text-center py-16">
        <Package className="h-16 w-16 mx-auto mb-4 text-gray-300 dark:text-gray-600" />
        <p className="text-lg font-medium text-gray-500 dark:text-gray-400">
          {searchTerm ? 'No matching products found' : 'No inventory items'}
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Enhanced Metric Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        <OverviewMetricCard
          title="Total Products"
          value={stats.totalProducts}
          subtitle={`${stats.productsWithDifference} with differences`}
          icon={Package}
          gradient="from-slate-500 to-slate-600"
          format="count"
        />
        <OverviewMetricCard
          title={`${warehouseLocation.name} Total`}
          value={stats.warehouseTotalQty}
          subtitle="Warehouse inventory"
          icon={Warehouse}
          gradient="from-blue-500 to-blue-600"
          format="count"
        />
        <OverviewMetricCard
          title={`${comparisonLocation.name} Total`}
          value={stats.otherLocationTotalQty}
          subtitle="Shop inventory"
          icon={Store}
          gradient="from-amber-500 to-amber-600"
          format="count"
        />
        <OverviewMetricCard
          title="Net Difference"
          value={stats.netDifference}
          subtitle={stats.netDifference > 0 ? 'More in shop' : stats.netDifference < 0 ? 'More in warehouse' : 'Balanced'}
          icon={GitCompare}
          trend={stats.netDifference !== 0 ? {
            value: Math.abs(stats.netDifference),
            positive: stats.netDifference > 0,
            label: stats.netDifference > 0 ? 'shop advantage' : 'warehouse advantage'
          } : undefined}
          gradient={stats.netDifference > 0 ? "from-emerald-500 to-emerald-600" : stats.netDifference < 0 ? "from-rose-500 to-rose-600" : "from-slate-500 to-slate-600"}
          format="count"
        />
      </div>
      
      {/* Additional Comparison Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <OverviewMetricCard
          title="Products with Differences"
          value={stats.productsWithDifference}
          subtitle={`${((stats.productsWithDifference / stats.totalProducts) * 100).toFixed(1)}% of total`}
          icon={AlertTriangle}
          gradient="from-amber-500 to-amber-600"
          format="count"
        />
        <OverviewMetricCard
          title="Warehouse Only"
          value={stats.warehouseOnlyCount}
          subtitle="Not in shop"
          icon={Warehouse}
          gradient="from-blue-500 to-indigo-600"
          format="count"
        />
        <OverviewMetricCard
          title="Shop Only"
          value={stats.otherLocationOnlyCount}
          subtitle="Not in warehouse"
          icon={Store}
          gradient="from-amber-500 to-orange-600"
          format="count"
        />
      </div>

      <div className="flex items-center justify-between mb-4 px-2">
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-blue-500/20 border-2 border-blue-500" />
            <span className="text-sm font-medium text-slate-600 dark:text-slate-400">{warehouseLocation.name} (Default)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-amber-500/20 border-2 border-amber-500" />
            <span className="text-sm font-medium text-slate-600 dark:text-slate-400">{comparisonLocation.name}</span>
          </div>
        </div>
        <div className="flex items-center gap-4 text-xs">
          <div className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
            <TrendingUp className="h-3.5 w-3.5" />
            <span>More in {comparisonLocation.name}</span>
          </div>
          <div className="flex items-center gap-1 text-rose-600 dark:text-rose-400">
            <TrendingDown className="h-3.5 w-3.5" />
            <span>Less in {comparisonLocation.name}</span>
          </div>
          <div className="flex items-center gap-1 text-slate-400">
            <Equal className="h-3.5 w-3.5" />
            <span>Equal</span>
          </div>
        </div>
      </div>

      <div className="card overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
        <table className="w-full min-w-[1100px]">
          <thead>
            <tr className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 border-b border-gray-200 dark:border-gray-700">
              <th className="px-4 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider sticky left-0 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 z-10 border-r border-gray-200 dark:border-gray-700" style={{ minWidth: '180px' }}>
                Product Name
              </th>
              <th className="px-3 py-4 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider" style={{ minWidth: '100px' }}>
                Reference
              </th>
              <th className="px-3 py-4 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider" style={{ minWidth: '90px' }}>
                Color
              </th>
              <th className="px-3 py-4 text-center text-xs font-semibold uppercase tracking-wider" style={{ minWidth: '90px' }}>
                <div className="flex flex-col items-center">
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300">
                    {warehouseLocation.name}
                  </span>
                </div>
              </th>
              <th className="px-3 py-4 text-center text-xs font-semibold uppercase tracking-wider" style={{ minWidth: '90px' }}>
                <div className="flex flex-col items-center">
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300">
                    {comparisonLocation.name}
                  </span>
                </div>
              </th>
              <th className="px-3 py-4 text-center text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider" style={{ minWidth: '80px' }}>
                Diff
              </th>
              {sizeColumns.map(size => (
                <th key={size} className="px-2 py-4 text-center text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider" style={{ minWidth: '100px' }}>
                  <div className="flex flex-col items-center gap-1">
                    <span>{size}</span>
                    <div className="flex gap-1 text-[10px]">
                      <span className="text-blue-500">W</span>
                      <span className="text-slate-300">/</span>
                      <span className="text-amber-500">O</span>
                    </div>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {filteredGroups.map((group, groupIndex) => (
              <React.Fragment key={group.productName}>
                {group.colors.map((colorVariant, colorIndex) => {
                  const isFirstColorRow = colorIndex === 0;
                  const isLastColorRow = colorIndex === group.colors.length - 1;

                  return (
                    <tr
                      key={`${group.productName}-${colorVariant.colorId}`}
                      className={`
                        hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors
                        ${getRowBackground(colorVariant.difference)}
                        ${isLastColorRow && groupIndex < filteredGroups.length - 1 ? 'border-b-2 border-slate-300 dark:border-slate-600' : ''}
                      `}
                    >
                      <td className={`px-4 py-3 sticky left-0 z-10 ${getRowBackground(colorVariant.difference)} ${!getRowBackground(colorVariant.difference) && (groupIndex % 2 === 0 ? 'bg-white dark:bg-gray-900' : 'bg-gray-50/30 dark:bg-gray-800/30')}`}>
                        {isFirstColorRow ? (
                          <div className="flex items-center gap-3">
                            {group.imageUrl && !imgErrors.has(group.productName) ? (
                              <img
                                src={group.imageUrl}
                                alt=""
                                loading="lazy"
                                className="w-10 h-10 rounded-lg object-cover border border-gray-200 dark:border-gray-700 shadow-sm flex-shrink-0"
                                onError={() => setImgErrors(prev => new Set(prev).add(group.productName))}
                              />
                            ) : (
                              <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 flex items-center justify-center flex-shrink-0">
                                <Package className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                              </div>
                            )}
                            <div className="font-semibold text-slate-900 dark:text-slate-100 truncate max-w-[160px]" title={group.productName}>
                              {group.productName}
                            </div>
                          </div>
                        ) : (
                          <span className="pl-[52px] text-slate-300 dark:text-slate-600">-</span>
                        )}
                      </td>
                      <td className="px-3 py-3">
                        {isFirstColorRow ? (
                          <span className="text-slate-600 dark:text-slate-400 text-sm truncate block max-w-[120px]" title={group.referenceCode}>
                            {group.referenceCode || '-'}
                          </span>
                        ) : (
                          <span className="text-slate-300 dark:text-slate-600">-</span>
                        )}
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-2">
                          {colorVariant.hexCode && (
                            <span
                              className="w-4 h-4 rounded-full border border-slate-300 dark:border-slate-600 flex-shrink-0"
                              style={{ backgroundColor: colorVariant.hexCode }}
                              title={colorVariant.hexCode}
                            />
                          )}
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                            {colorVariant.colorName}
                          </span>
                        </div>
                      </td>
                      <td className="px-3 py-3 text-center">
                        <span className="inline-flex items-center justify-center min-w-[48px] px-3 py-1 rounded-lg bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-200 font-bold tabular-nums">
                          {colorVariant.warehouseQty}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-center">
                        <span className="inline-flex items-center justify-center min-w-[48px] px-3 py-1 rounded-lg bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200 font-bold tabular-nums">
                          {colorVariant.otherQty}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-center">
                        <DifferenceIndicator diff={colorVariant.difference} />
                      </td>
                      {sizeColumns.map(size => {
                        const sizeData = colorVariant.sizeBreakdown.find(s => s.size === size);
                        const wQty = sizeData?.warehouseQty || 0;
                        const oQty = sizeData?.otherQty || 0;
                        const diff = oQty - wQty;

                        return (
                          <td key={size} className="px-2 py-3 text-center">
                            {(wQty > 0 || oQty > 0) ? (
                              <div className="flex flex-col items-center gap-0.5">
                                <div className="flex items-center gap-1 text-xs tabular-nums">
                                  <span className="text-blue-600 dark:text-blue-400 font-medium">{wQty}</span>
                                  <span className="text-slate-300 dark:text-slate-600">/</span>
                                  <span className="text-amber-600 dark:text-amber-400 font-medium">{oQty}</span>
                                </div>
                                <div className="text-[10px]">
                                  <DifferenceIndicator diff={diff} showText={false} />
                                </div>
                              </div>
                            ) : (
                              <span className="text-slate-200 dark:text-slate-700">-</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-200 dark:border-gray-700">
        <div className="flex flex-col sm:flex-row gap-2 text-sm text-gray-600 dark:text-gray-400">
          <span className="font-medium">
            Showing <span className="text-gray-900 dark:text-gray-100">{filteredGroups.length}</span> products
          </span>
          {searchTerm && (
            <>
              <span className="hidden sm:inline">•</span>
              <span className="text-blue-600 dark:text-blue-400">
                Filtered by "{searchTerm}"
              </span>
            </>
          )}
        </div>
        <div className="flex items-center gap-2 text-sm">
          <span className="text-gray-600 dark:text-gray-400">Net difference:</span>
          <span className={`font-semibold ${stats.netDifference > 0 ? 'text-emerald-600 dark:text-emerald-400' : stats.netDifference < 0 ? 'text-rose-600 dark:text-rose-400' : 'text-gray-900 dark:text-gray-100'}`}>
            {stats.netDifference > 0 ? '+' : ''}{stats.netDifference.toLocaleString()}
          </span>
        </div>
      </div>
    </div>
  );
};

export interface ComparisonExportData {
  groups: ProductComparisonGroup[];
  sizeColumns: string[];
  stats: ComparisonStats;
  warehouseLocation: { id: string; name: string };
  comparisonLocation: { id: string; name: string };
}

export function prepareComparisonExportData(
  products: ProductWithLocationQuantities[],
  warehouseLocation: { id: string; name: string },
  comparisonLocation: { id: string; name: string },
  colors: { id: string; name: string; hex_code?: string }[],
  sizes: { id: string; name: string; sort_order?: number }[],
  stats: ComparisonStats
): ComparisonExportData {
  const getColorInfo = (colorId: string | undefined) => {
    if (!colorId) return undefined;
    return colors.find(c => c.id === colorId);
  };

  const getSizeName = (sizeId: string | undefined): string | undefined => {
    if (!sizeId) return undefined;
    return sizes.find(s => s.id === sizeId)?.name;
  };

  const sortedSizes = [...sizes].sort((a, b) => {
    if (a.sort_order !== undefined && b.sort_order !== undefined) {
      return a.sort_order - b.sort_order;
    }
    return a.name.localeCompare(b.name);
  });

  const groupsMap: Record<string, ProductComparisonGroup> = {};

  products.forEach(product => {
    const groupKey = product.name || 'Unnamed Product';
    const warehouseQty = product.locationQuantities[warehouseLocation.id] || 0;
    const otherQty = product.locationQuantities[comparisonLocation.id] || 0;

    if (!groupsMap[groupKey]) {
      groupsMap[groupKey] = {
        productName: product.name || 'Unnamed Product',
        referenceCode: product.reference_code || '',
        category: product.category || '',
        imageUrl: product.image_url,
        sellPrice: product.sell_price || 0,
        buyPrice: product.buy_price || 0,
        warehouseQty: 0,
        otherQty: 0,
        difference: 0,
        colors: []
      };
    }

    const colorInfo = getColorInfo(product.color_id);
    const colorKey = product.color_id || 'no-color';
    const sizeName = getSizeName(product.size_id) || 'No Size';

    let colorVariant = groupsMap[groupKey].colors.find(c => c.colorId === colorKey);
    if (!colorVariant) {
      colorVariant = {
        colorId: colorKey,
        colorName: colorInfo?.name || '-',
        hexCode: colorInfo?.hex_code,
        warehouseQty: 0,
        otherQty: 0,
        difference: 0,
        sizeBreakdown: []
      };
      groupsMap[groupKey].colors.push(colorVariant);
    }

    colorVariant.warehouseQty += warehouseQty;
    colorVariant.otherQty += otherQty;
    colorVariant.difference = colorVariant.otherQty - colorVariant.warehouseQty;

    let sizeEntry = colorVariant.sizeBreakdown.find(s => s.size === sizeName);
    if (!sizeEntry) {
      sizeEntry = { size: sizeName, warehouseQty: 0, otherQty: 0, difference: 0 };
      colorVariant.sizeBreakdown.push(sizeEntry);
    }
    sizeEntry.warehouseQty += warehouseQty;
    sizeEntry.otherQty += otherQty;
    sizeEntry.difference = sizeEntry.otherQty - sizeEntry.warehouseQty;

    groupsMap[groupKey].warehouseQty += warehouseQty;
    groupsMap[groupKey].otherQty += otherQty;
    groupsMap[groupKey].difference = groupsMap[groupKey].otherQty - groupsMap[groupKey].warehouseQty;

    if (product.reference_code && !groupsMap[groupKey].referenceCode) {
      groupsMap[groupKey].referenceCode = product.reference_code;
    }
    if (product.image_url && !groupsMap[groupKey].imageUrl) {
      groupsMap[groupKey].imageUrl = product.image_url;
    }
    if (product.sell_price && groupsMap[groupKey].sellPrice === 0) {
      groupsMap[groupKey].sellPrice = product.sell_price;
    }
    if (product.buy_price && groupsMap[groupKey].buyPrice === 0) {
      groupsMap[groupKey].buyPrice = product.buy_price;
    }
    if (product.category && !groupsMap[groupKey].category) {
      groupsMap[groupKey].category = product.category;
    }
  });

  const groups = Object.values(groupsMap);
  groups.sort((a, b) => a.productName.localeCompare(b.productName));
  groups.forEach(group => {
    group.colors.sort((a, b) => a.colorName.localeCompare(b.colorName));
  });

  const usedSizes = new Set<string>();
  groups.forEach(group => {
    group.colors.forEach(color => {
      color.sizeBreakdown.forEach(sb => {
        if (sb.size !== 'No Size') {
          usedSizes.add(sb.size);
        }
      });
    });
  });

  const hasNoSize = groups.some(group =>
    group.colors.some(color => color.sizeBreakdown.some(sb => sb.size === 'No Size'))
  );

  const sizeColumns = sortedSizes.filter(s => usedSizes.has(s.name)).map(s => s.name);
  if (hasNoSize) sizeColumns.push('No Size');

  return { groups, sizeColumns, stats, warehouseLocation, comparisonLocation };
}
